import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Menu, 
  X, 
  BookOpen, 
  User, 
  LogOut, 
  LayoutDashboard,
  ChevronDown
} from 'lucide-react';

const hubLinks = [
  { path: '/gospel', label: 'Gospel', description: 'Foundation of grace' },
  { path: '/identity', label: 'Identity', description: 'Who you are in Christ' },
  { path: '/hermeneutics', label: 'Hermeneutics', description: 'Reading Scripture' },
  { path: '/exegesis', label: 'Exegesis Labs', description: 'Deep study' },
  { path: '/what-now', label: 'What Now?', description: 'Gospel living' },
  { path: '/resources', label: 'Resources', description: 'Tools & materials' },
  { path: '/leaders', label: 'For Leaders', description: 'Teaching guides' },
];

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 glass-nav" role="navigation" aria-label="Main navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 text-primary hover:opacity-80 transition-opacity"
            data-testid="nav-logo"
          >
            <BookOpen className="h-6 w-6" strokeWidth={1.5} />
            <span className="font-serif text-xl font-semibold tracking-tight">Gospel Hub</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-1 text-foreground/80 hover:text-foreground"
                  data-testid="nav-hubs-dropdown"
                >
                  Hubs <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {hubLinks.map((hub) => (
                  <DropdownMenuItem key={hub.path} asChild>
                    <Link 
                      to={hub.path} 
                      className="flex flex-col items-start py-2"
                      data-testid={`nav-hub-${hub.path.slice(1)}`}
                    >
                      <span className="font-medium">{hub.label}</span>
                      <span className="text-xs text-muted-foreground">{hub.description}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {isAuthenticated && (
              <Link to="/dashboard">
                <Button 
                  variant="ghost" 
                  className={`text-foreground/80 hover:text-foreground ${isActive('/dashboard') ? 'bg-muted' : ''}`}
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
            )}
          </div>

          {/* Auth Buttons - Desktop */}
          <div className="hidden lg:flex items-center gap-2">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2"
                    data-testid="nav-user-menu"
                  >
                    <User className="h-4 w-4" />
                    <span className="max-w-[100px] truncate">{user?.name}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center" data-testid="menu-dashboard">
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-destructive focus:text-destructive cursor-pointer"
                    data-testid="menu-logout"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" data-testid="nav-login">
                    Log In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-hover-lift" data-testid="nav-register">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            data-testid="nav-mobile-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border mobile-menu-enter" role="menu">
            <div className="flex flex-col gap-2">
              <p className="px-3 py-2 text-sm font-medium text-muted-foreground">Hubs</p>
              {hubLinks.map((hub) => (
                <Link
                  key={hub.path}
                  to={hub.path}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive(hub.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${hub.path.slice(1)}`}
                  role="menuitem"
                >
                  {hub.label}
                </Link>
              ))}
              
              <div className="border-t border-border my-2" />
              
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="px-3 py-2 rounded-md text-sm font-medium text-foreground hover:bg-muted flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-dashboard"
                    role="menuitem"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-3 py-2 rounded-md text-sm font-medium text-destructive hover:bg-destructive/10 flex items-center gap-2 text-left"
                    data-testid="mobile-nav-logout"
                    role="menuitem"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-3 py-2 rounded-md text-sm font-medium text-foreground hover:bg-muted"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-login"
                    role="menuitem"
                  >
                    Log In
                  </Link>
                  <Link
                    to="/register"
                    className="px-3 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-register"
                    role="menuitem"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
